import * as React from 'react';
import notify from '../../../../shared/feedback/notifications';
import Api from '../../../../shared/Api';
import Autocomplete from '@mui/material/Autocomplete';
import Button from '@mui/material/Button';
import CloseIcon from '@mui/icons-material/Close';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import { Photographer } from '../BookingsCalendarTypes';
import TextField from '@mui/material/TextField';
import { Translate } from '../../../../shared/LanguageService';
import WysiwygEditor from '../../../../components/common/editor/Editor';
import { styled } from '@mui/material/styles';
import { useQuery } from 'react-query';

type EmailModalLabels = {
    emailTemplate: string;
    emailWhen: string;
    emailClose: string;
    emailSend: string;
    emailSuccess: string;
    emailError: string;
    emailSubject: string;
    emailCC: string;
    emailTitle: (name: string, email: string) => string;
    emailSender: string;
    emailCcError: string;
    emailExpiredDate: string;
};

type Sender = { displayNameTranslationKey: string, emailAddresses: { address: string; }[]; };

type EmailModalProps = {
    showEmailModal: boolean;
    closeEmailModal: () => void;
    photographer: Photographer;
    labels: EmailModalLabels;
    emailTemplates: { content: string, key: number, name: string; }[];
    senders: Sender[];
    createEmail: (
        fromEmail: string,
        toEmail: string,
        subject: string,
        body: string,
        cc?: string,
        desiredSendTime?: Date
    ) => Promise<any>;
};

export const StyledTextField = styled(TextField)(({ theme }) => ({
    'fieldset legend span': {
        textTransform: 'lowercase'
    }
}));

const EmailModal = ({ showEmailModal, closeEmailModal, photographer, labels, emailTemplates, createEmail, senders }: EmailModalProps) => {

    const [desiredSendTime, setDesiredSendTime] = React.useState<Date | null>(null);
    const [cc, setCc] = React.useState<string>('');
    const [subject, setSubject] = React.useState<string>('');
    const [sender, setSender] = React.useState<Sender | null>(null);
    const [body, setBody] = React.useState<string>('');
    const [emailTemplate, setEmailTemplate] = React.useState<object | null>(null);
    const [defaultTemplate, setDefaultTemplate] = React.useState<string>('');
    const [errorDate, setErrorDate] = React.useState<boolean>(false);
    const [errorCc, setErrorCc] = React.useState<boolean>(false);
    const [key, setKey] = React.useState<number>(0);

    React.useEffect(() => {
        if (emailTemplates && !body) {
            let defaultTemplate = emailTemplates.find(
                (x) => x.key === 15 /* Default template for emails */
            );
            setDefaultTemplate(defaultTemplate.content);
            setBody(defaultTemplate.content);
        }
    }, [emailTemplates, body]);

    const handleDesiredTimeChange = (newValue) => {
        setDesiredSendTime(newValue);
        if (newValue < new Date(Date.now())
            || new Date(newValue).toDateString() === 'Invalid Date') {
            setErrorDate(true);
        }
        if (newValue >= new Date(Date.now())
            || newValue === null) {
            setErrorDate(false);
        }
    };

    const handleCcChange = (e) => {
        const value = e.target.value;
        let currentEmails = value.split(/,| /).filter((e) => e && e.trim());
        setErrorCc(false);
        setCc(value);
        for (let i = 0; i < currentEmails.length; i++) {
            let regex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]+$/i;
            if (regex.test(currentEmails[i].replace(/\s/g, ''))) {
                currentEmails[i] = true;
            } else {
                currentEmails[i] = false;
            }
        }
        currentEmails.forEach((email) => {
            if (!email) {
                setErrorCc(true);
            }
        });
    };


    const handleEmailTemplateChange = (event, value) => {
        setEmailTemplate(value ? value : null);
        setBody(value ? value.content : defaultTemplate);
        setKey(key + 1);
    };

    const handleSenderChange = (event, value) => {
        setSender(value);
    };

    const handleBodyChange = (value) => {
        setBody(value);
    };

    const handleClose = () => {
        setBody(null);
        setSubject('');
        setCc('');
        setKey(0);
        setEmailTemplate(null);
        setSender(null);
        setDesiredSendTime(null);
        setErrorDate(false);
        setErrorCc(false);
        closeEmailModal();
    };

    const sendEmail = () => {
        const fromEmail = sender ? sender.emailAddresses[0].address : senders[0].emailAddresses[0].address;
        if (body) {
            const result = createEmail(fromEmail, photographer.email, subject, body, cc, desiredSendTime);
            result.then(() => {
                notify.success(labels.emailSuccess);
                handleClose();
            })
                .catch(() => {
                    notify.failure(labels.emailError);
                });
        }
    };

    return (
        <Dialog onClose={handleClose} open={showEmailModal} fullWidth maxWidth='xl' sx={{ '& .MuiPaper-root': { overflowY: 'visible', maxHeight: 'fit-content' } }}>
            <DialogTitle>{labels.emailTitle(photographer.name, photographer.email)}
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers>
                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        {senders
                            ? <Autocomplete onChange={handleSenderChange}
                                disablePortal
                                fullWidth
                                id="email-sender"
                                value={sender}
                                getOptionLabel={(option) => option.tranlatedDisplayName || ''}
                                isOptionEqualToValue={(option, value) => option.emailAddresses[0].address === value.emailAddresses[0].address || value.emailAddresses[0].address === null}
                                options={senders}
                                sx={{
                                    '& svg': {
                                        zIndex: '1'
                                    }
                                }}
                                renderInput={(params) => <StyledTextField {...params} label={labels.emailSender} />}
                            />
                            : null
                        }
                    </Grid>
                    <Grid item xs={6}>
                        <StyledTextField fullWidth
                            error={errorCc}
                            helperText={errorCc ? labels.emailCcError : ''}
                            value={cc}
                            onChange={handleCcChange}
                            id="email-cc"
                            label={labels.emailCC}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <DateTimePicker
                            label={labels.emailWhen}
                            value={desiredSendTime}
                            inputFormat="yyyy-MM-dd HH:mm"
                            disablePast
                            onChange={handleDesiredTimeChange}
                            renderInput={(params) => (
                                <StyledTextField fullWidth {...params}
                                    error={errorDate}
                                    helperText={errorDate ? labels.emailExpiredDate : null}
                                    sx={{
                                        '& svg': {
                                            zIndex: '1'
                                        }
                                    }}
                                />
                            )}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        {emailTemplates
                            ? <Autocomplete onChange={handleEmailTemplateChange}
                                disablePortal
                                fullWidth
                                id="email-template"
                                value={emailTemplate}
                                getOptionLabel={(option) => option.name || ''}
                                isOptionEqualToValue={(option, value) => option.content === value.content || value.content === null}
                                options={emailTemplates}
                                sx={{
                                    '& svg': {
                                        zIndex: '1'
                                    }
                                }}
                                renderInput={(params) => <StyledTextField {...params} label={labels.emailTemplate} />}
                            />
                            : null
                        }
                    </Grid>
                    <Grid item xs={12}>
                        <StyledTextField fullWidth
                            value={subject}
                            onChange={(e) => { setSubject(e.target.value); }}
                            id="email-subject"
                            label={labels.emailSubject}
                        />
                    </Grid>
                    {body
                        ? <Grid item xs={12} sx={{
                            '& .tox-tinymce': {
                                borderRadius: '4px'
                            },
                            '& .tox .tox-toolbar__primary, .tox .tox-statusbar': {
                                backgroundColor: 'rgb(250, 250, 250)'
                            },
                            '& .tox-tbtn[title|="Code source"]': {
                                display: 'none'
                            },
                            '& .tox-tbtn': {
                                cursor: 'pointer'
                            },
                            '& .tox-statusbar__path': {
                                visibility: 'hidden'
                            }
                        }}>
                            <WysiwygEditor
                                key={key}
                                onChange={handleBodyChange}
                                content={body}
                                height={300}
                                addImageModal={false}
                                onImageUploaded={() => { }}
                            />
                        </Grid>
                        : null
                    }
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button autoFocus onClick={handleClose}>
                    {labels.emailClose}
                </Button>
                <Button autoFocus onClick={sendEmail} variant="contained" disabled={errorDate || errorCc}>
                    {labels.emailSend}
                </Button>
            </DialogActions>
        </Dialog >
    );
};

const EmailModalDataLayer = (props) => {
    const { photographer } = props;
    const getEmailTemplates = (photographerId: number) => {
        return useQuery('templates', () => new Api().get('/views/photographer-calendars/email-templates/' + photographerId));
    };
    const getSenders = () => {
        return useQuery('senders', () => new Api().get('/views/photographer-calendars/email-senders/'));
    };
    const emailTemplates = getEmailTemplates(photographer.id);
    const senders = getSenders();

    let translatedSenders = null;

    if (senders.data) {
        translatedSenders = [...senders.data.senders];
        translatedSenders.forEach(sender => {
            sender.tranlatedDisplayName = Translate(sender.displayNameTranslationKey) +
                ` <${sender.emailAddresses[0].address}>`;
        });
    }

    const createEmail = (fromEmail: string, toEmail: string, subject: string, body: string, cc?: string, desiredSendTime?: Date) => {
        return new Api().post('/views/photographer-calendars/email', {
            fromEmail, toEmail, subject, body, cc, desiredSendTime
        });
    };

    return (
        <EmailModal
            {...props}
            emailTemplates={emailTemplates.data}
            senders={translatedSenders}
            createEmail={createEmail}
        />
    );

};

const EmailModalTranslationLayer = (props): any => {
    let labels: EmailModalLabels = {
        emailTemplate: Translate('photographer-mail-modal/template'),
        emailWhen: Translate('photographer-mail-modal/when'),
        emailClose: Translate('photographer-mail-modal/close'),
        emailSend: Translate('photographer-mail-modal/send'),
        emailSuccess: Translate('photographer-mail-modal/success'),
        emailError: Translate('photographer-mail-modal/error'),
        emailSubject: Translate('photographer-mail-modal/subject'),
        emailCC: Translate('photographer-mail-modal/cc'),
        emailTitle: (name, email) => Translate('photographer-mail-modal/title', { name, email }),
        emailSender: Translate('photographer-mail-modal/sender'),
        emailCcError: Translate('photographer-mail-modal/cc-error'),
        emailExpiredDate: Translate('photographer-mail-modal/expired-date')
    };

    return <EmailModalDataLayer {...props} labels={labels} />;
};

export default EmailModalTranslationLayer;